import { uniqueId } from "lodash";
import { AdjustAction } from "@/components/Product/MerchandiseInventory/adjustInventoryActions";

export type ReturnAction = typeof AdjustAction.MOVE_TO_AVAILABLE | typeof AdjustAction.MOVE_TO_UNAVAILABLE;

export interface ReturnAdjustmentRow {
    id: string;
    action: ReturnAction;
    reason: string;
    qty: number;
}

export type ReturnAdjustmentsByTemplateId = Record<string, ReturnAdjustmentRow[]>;

export const RETURN_ACTION_OPTIONS: ReturnAction[] = [AdjustAction.MOVE_TO_AVAILABLE, AdjustAction.MOVE_TO_UNAVAILABLE];

export const RETURN_REASONS: Record<ReturnAction, string[]> = {
    [AdjustAction.MOVE_TO_AVAILABLE]: ["Correction", "Return/Restock"],
    [AdjustAction.MOVE_TO_UNAVAILABLE]: ["Correction", "Damaged"],
};

const DEFAULT_MOVE_TO_AVAILABLE_REASON =
    RETURN_REASONS[AdjustAction.MOVE_TO_AVAILABLE][1] ?? RETURN_REASONS[AdjustAction.MOVE_TO_AVAILABLE][0];

export const getVariantAdjustmentSync = (
    rows: ReturnAdjustmentRow[] | undefined,
    targetQty: number,
): ReturnAdjustmentRow[] => {
    if (targetQty <= 0) {
        return [];
    }

    const sanitizedRows = (rows ?? []).map((row) => ({
        ...row,
        qty: Math.max(1, row.qty),
    }));

    // On first reduction of fulfilled add default Return/Restock row
    if (sanitizedRows.length === 0) {
        return [
            {
                id: uniqueId("return-row-"),
                action: AdjustAction.MOVE_TO_AVAILABLE,
                reason: DEFAULT_MOVE_TO_AVAILABLE_REASON,
                qty: targetQty,
            },
        ];
    }

    const syncedRows = sanitizedRows.map((row) => ({ ...row }));
    const currentTotalQty = syncedRows.reduce((sum, row) => sum + row.qty, 0);
    const qtyDelta = targetQty - currentTotalQty;

    if (qtyDelta > 0) {
        // Add missing quantity to the first row to keep user row choices intact.
        syncedRows[0].qty += qtyDelta;
        return syncedRows;
    }

    if (qtyDelta === 0) {
        return syncedRows;
    }

    let remainingQtyToRemove = Math.abs(qtyDelta);

    // Reduce from the end so earlier rows (especially the first) remain as stable as possible.
    for (let rowIndex = syncedRows.length - 1; rowIndex >= 0 && remainingQtyToRemove > 0; rowIndex -= 1) {
        const minimumAllowedQty = rowIndex === 0 ? 1 : 0;
        const reducibleQty = syncedRows[rowIndex].qty - minimumAllowedQty;

        if (reducibleQty <= 0) {
            continue;
        }

        const qtyDecrement = Math.min(reducibleQty, remainingQtyToRemove);
        syncedRows[rowIndex].qty -= qtyDecrement;
        remainingQtyToRemove -= qtyDecrement;

        if (syncedRows[rowIndex].qty === 0) {
            syncedRows.splice(rowIndex, 1);
        }
    }

    return syncedRows;
};

export const hasAdjustmentRowsChanged = (
    previousRows: ReturnAdjustmentRow[] | undefined,
    nextRows: ReturnAdjustmentRow[],
): boolean => {
    const safePreviousRows = previousRows ?? [];

    if (nextRows.length !== safePreviousRows.length) {
        return true;
    }

    return nextRows.some((row, index) => {
        const previousRow = safePreviousRows[index];

        return (
            row.id !== previousRow?.id ||
            row.action !== previousRow?.action ||
            row.reason !== previousRow?.reason ||
            row.qty !== previousRow?.qty
        );
    });
};
